import {
  Box,
  Drawer,
  IconButton,
  Typography
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useEffect, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { translateText } from '../../../language'
import { Loading } from '../widgets'
import { fetchArea, fetchAreas } from '../../../api/parking'
import { setSelectedArea } from '../../../state/parking'
import { useThemeMode } from '../../../theme'

export const ParkingView = () => {
  const dispatch = useDispatch()
  const { i18n } = useTranslation()
  const { areas, selectedArea, error } = useSelector((state) => state.parking)
  const config = useSelector((state) => state.config?.config)
  const svgRef = useRef(null)
  const [svgContent, setSvgContent] = useState(null)
  const { palette } = useThemeMode()

  // Fetch areas on mount if empty
  useEffect(() => {
    if (areas.length === 0 && config) {
      fetchAreas(config)
    }
  }, [areas.length, config])

  // Fetch SVG file once
  useEffect(() => {
    fetch(`/parking-web.svg?v=${config?.version ?? '0'}`)
      .then((res) => res.text())
      .then((text) => setSvgContent(text))
      .catch((err) => console.error('Failed to load parking SVG', err))
  }, [config?.version])

  // Bind click handlers after SVG is injected and areas loaded
  useEffect(() => {
    if (!svgRef.current || !svgContent || areas.length === 0) return

    areas.forEach((area) => {
      const el = svgRef.current?.querySelector(
        `#parking-label-${area.name.toLowerCase()}`
      )
      if (el) {
        el.style.cursor = 'pointer'
        el.onclick = () => dispatch(fetchArea(area.id))
      }
    })
  }, [svgContent, areas, dispatch])

  // Style SVG internals: inject border rect and recolour parking circles
  useEffect(() => {
    if (!svgRef.current || !svgContent) return

    const svgEl = svgRef.current.querySelector('svg')
    if (!svgEl) return

    // One-time setup: expand viewBox to add ~5px visual padding, store original bounds
    const PADDING = 16
    const INSET = 6
    if (!svgEl.dataset.origViewbox) {
      const vb = svgEl.viewBox.baseVal
      const [ox, oy, ow, oh] = [vb.x, vb.y, vb.width, vb.height]
      svgEl.dataset.origViewbox = `${ox} ${oy} ${ow} ${oh}`
      svgEl.setAttribute('viewBox', `${ox - PADDING} ${oy - PADDING} ${ow + PADDING * 2} ${oh + PADDING * 2}`)

      const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect')
      rect.setAttribute('id', 'svg-border-rect')
      rect.setAttribute('x', ox + INSET)
      rect.setAttribute('y', oy + INSET)
      rect.setAttribute('width', ow - INSET * 2)
      rect.setAttribute('height', oh - INSET * 2)
      rect.setAttribute('fill', 'none')
      rect.setAttribute('stroke', palette.mapBackground)
      rect.setAttribute('stroke-width', '12')
      svgEl.appendChild(rect)
    } else {
      const borderRect = svgEl.querySelector('#svg-border-rect')
      if (borderRect) borderRect.setAttribute('stroke', palette.mapBackground)
    }

    svgRef.current.querySelectorAll('[id^="parking-label-"] circle').forEach((circle) => {
      circle.style.fill = palette.headerColor
    })
  }, [svgContent, palette.mapBackground, palette.headerColor])

  const handleClose = () => {
    dispatch(setSelectedArea(null))
  }

  if (!svgContent) {
    return <Loading />
  }

  if (error) {
    return (
      <Box sx={{ padding: '16px' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ width: '100%' }}>
      <style>{'.parking:hover { opacity: 0.75; }'}</style>

      <Box sx={{
        overflow: 'hidden',
        maxHeight: 'calc(100vh - 50px - 64px - 64px)',
        '@supports (height: 100dvh)': {
          maxHeight: 'calc(100dvh - 50px - 64px - 64px)'
        }
      }}>
        <Box
          ref={svgRef}
          dangerouslySetInnerHTML={{ __html: svgContent }}
          sx={{
            width: '100%',
            '& svg': {
              width: '100%',
              height: 'auto',
              display: 'block',
              maxHeight: 'calc(100vh - 50px - 64px - 64px)',
              '@supports (height: 100dvh)': {
                maxHeight: 'calc(100dvh - 50px - 64px - 64px)'
              }
            }
          }}
        />
      </Box>

      <Drawer
        anchor="top"
        open={selectedArea !== null}
        onClose={handleClose}
        PaperProps={{ sx: { maxHeight: '60vh', borderRadius: '0 0 12px 12px', background: palette.paperColor } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px' }}>
          <Typography variant="h6" sx={{ color: palette.headerColor, fontWeight: 700 }}>
            Parking {selectedArea?.name}
          </Typography>
          <IconButton onClick={handleClose} aria-label="close" sx={{ color: palette.headerColor }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ padding: '0 16px 16px', overflow: 'auto' }}>
          <ReactMarkdown>
            {translateText(selectedArea?.notes ?? [], i18n)}
          </ReactMarkdown>
        </Box>
        <Box sx={{ width: '40px', height: '4px', background: palette.paperColorDivider, borderRadius: '2px', margin: '8px auto' }} />
      </Drawer>
    </Box>
  )
}
